import { NodeProps, useReactFlow } from '@xyflow/react'
import {
  Play,
  Copy,
  Settings as SettingsIcon,
  Calendar,
  BarChart3,
} from 'lucide-react'
import React, { useState, useEffect, memo, useRef, useMemo } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'

// --- Data Settings imports ---
import useDataSettings from '@/hooks/useDataSettings'
import { AICommandType, createAICommand, useAICommandState } from '@/utils/aiCommandUtils'
import NodeBaseContainer from '../base/base-container/NodeBaseContainer'
import NodeDataSettingsDialog from '../../dialogs/data-settings-dialog/NodeDataSettingsDialog'
import SchedulingDialog from '../../dialogs/SchedulingDialog'

interface UsageCounters {
  totalRuns: number
  successfulRuns: number
  failedRuns: number
  avgProcessingTimeMs: number
  avgInputSizeBytes: number
  avgOutputSizeBytes: number
  totalDataProcessedBytes: number
  lastRunTimestamp?: number
}

interface NodeAIKnotData {
  selectedTemplate?: string
  customOperation?: string
  llmPrompt?: string
  templateConfig?: any
  inputMode: 'strict' | 'loose'
  outputMode: 'strict' | 'loose'
  inputJsonStructure?: string  // For strict input mode
  outputJsonStructure?: string // For strict output mode
  inputFormatPrompt?: string   // For loose input mode
  outputFormatPrompt?: string  // For loose output mode
  outputText?: string
  automationEnabled?: boolean
  schedule?: any
  statusMessage?: string
  usageCounters?: UsageCounters
  assignedEmails?: string[] | string
  dataSettings?: any
  isLocked?: boolean
  isMinimized?: boolean
  minimizeLevel?: 'normal' | 'medium' | 'minimum'
  documentCommand?: {
    'sysagent-cmd': string
    'sysagent-response'?: string
    processing?: boolean
    timestamp?: number
  }
}

// AI Operation Templates (enhanced with full configuration)
const AI_TEMPLATES = {
  "Set Operations": [
    { 
      value: "union", 
      label: "Union Node", 
      description: "Combines two sets, removing duplicates (A ∪ B)",
      llm_prompt: "Combine all unique elements from the input sets, removing duplicates. Return the result as a comma-separated list of unique elements.",
      inputs: {
        count: 2,
        data: [
          {
            type: "array",
            name: "set_a",
            prompt: "First set of elements",
            value: ["apple", "banana", "cherry"],
            meta: {}
          },
          {
            type: "array", 
            name: "set_b",
            prompt: "Second set of elements",
            value: ["banana", "cherry", "date"],
            meta: {}
          }
        ]
      },
      output: {
        count: 1,
        data: {
          type: "array",
          name: "union_result",
          prompt: "Combined unique elements from both sets",
          value: ["apple", "banana", "cherry", "date"],
          meta: {}
        }
      }
    },
    { 
      value: "intersection", 
      label: "Intersection Node", 
      description: "Returns elements common to both sets (A ∩ B)",
      llm_prompt: "Find elements that appear in all input sets. Return only the elements that exist in both sets as a comma-separated list.",
      inputs: {
        count: 2,
        data: [
          {
            type: "array",
            name: "set_a", 
            prompt: "First set of elements",
            value: ["apple", "banana", "cherry"],
            meta: {}
          },
          {
            type: "array",
            name: "set_b",
            prompt: "Second set of elements", 
            value: ["banana", "cherry", "date"],
            meta: {}
          }
        ]
      },
      output: {
        count: 1,
        data: {
          type: "array",
          name: "intersection_result",
          prompt: "Elements common to both sets",
          value: ["banana", "cherry"],
          meta: {}
        }
      }
    }
  ],
  "Preprocessing": [
    { 
      value: "parser", 
      label: "Parser Node", 
      description: "Parses and cleans raw data (e.g., HTML, JSON)",
      llm_prompt: "Parse and clean the provided raw data. Remove unnecessary markup, extract meaningful content, and return it in a structured format.",
      inputs: {
        count: 1,
        data: {
          type: "text",
          name: "raw_data",
          prompt: "Raw data to parse and clean",
          value: "<html><body><h1>Title</h1><p>Content here</p></body></html>",
          meta: {}
        }
      },
      output: {
        count: 1,
        data: {
          type: "json",
          name: "parsed_data",
          prompt: "Cleaned and structured data",
          value: {"title": "Title", "content": "Content here"},
          meta: {}
        }
      }
    }
  ],
  "Analysis": [
    { 
      value: "sentiment", 
      label: "Sentiment Analysis Node", 
      description: "Analyzes the sentiment of text (positive, negative, neutral)",
      llm_prompt: "Analyze the sentiment of the provided input text. Determine if it is positive, negative, or neutral, and return the result as a single word: 'positive', 'negative', or 'neutral'.",
      inputs: {
        count: 1,
        data: {
          type: "text",
          name: "input_text",
          prompt: "Provide text to analyze for sentiment",
          value: "This is amazing!",
          meta: {}
        }
      },
      output: {
        count: 1,
        data: {
          type: "text",
          name: "sentiment_result",
          prompt: "Sentiment of the input text",
          value: "positive",
          meta: {}
        }
      }
    },
    { 
      value: "entities", 
      label: "Entity Recognition Node", 
      description: "Extracts named entities from text (e.g., persons, locations)",
      llm_prompt: "Extract named entities from the provided text. Identify persons, locations, organizations, and other named entities. Return them as a structured JSON object with categories.",
      inputs: {
        count: 1,
        data: {
          type: "text",
          name: "input_text",
          prompt: "Text to analyze for named entities",
          value: "John Smith visited New York and worked at Microsoft.",
          meta: {}
        }
      },
      output: {
        count: 1,
        data: {
          type: "json",
          name: "entities_result",
          prompt: "Extracted named entities by category",
          value: {"persons": ["John Smith"], "locations": ["New York"], "organizations": ["Microsoft"]},
          meta: {}
        }
      }
    }
  ],
  "Generation": [
    { 
      value: "summarization", 
      label: "Summarization Node", 
      description: "Summarizes long text into a concise summary",
      llm_prompt: "Summarize the provided text into 1-2 concise sentences that capture the main points and key information.",
      inputs: {
        count: 1,
        data: {
          type: "text",
          name: "input_text",
          prompt: "Long text to summarize",
          value: "This is a very long article about artificial intelligence and its applications in modern technology...",
          meta: {}
        }
      },
      output: {
        count: 1,
        data: {
          type: "text",
          name: "summary_result",
          prompt: "Concise summary of the input text",
          value: "This article discusses AI applications in modern technology.",
          meta: {}
        }
      }
    }
  ],
  "Other": [
    { 
      value: "translation", 
      label: "Translation Node", 
      description: "Translates text to a target language",
      llm_prompt: "Translate the provided text to the specified target language. Maintain the original meaning and context while providing an accurate translation.",
      inputs: {
        count: 2,
        data: [
          {
            type: "text",
            name: "input_text",
            prompt: "Text to translate",
            value: "Hello, how are you?",
            meta: {}
          },
          {
            type: "text",
            name: "target_language",
            prompt: "Target language for translation",
            value: "Spanish",
            meta: {}
          }
        ]
      },
      output: {
        count: 1,
        data: {
          type: "text",
          name: "translation_result",
          prompt: "Translated text",
          value: "Hola, ¿cómo estás?",
          meta: {}
        }
      }
    }
  ]
}

const NodeAIKnot: React.FC<NodeProps & { data: NodeAIKnotData }> = (props) => {
  const { id, data, selected } = props
  const { updateNodeData } = useReactFlow()

  // Track if label has been initialized to prevent infinite loops
  const [labelInitialized, setLabelInitialized] = useState(false)

  // Initialize label
  useEffect(() => {
    if (!labelInitialized && (!data.label || data.label === 'AI Knot')) {
      setLabelInitialized(true)
      updateNodeData(id, { ...data, label: 'AI Knot' })
    }
  }, [id, data, updateNodeData, labelInitialized])

  const [isLocked, setIsLocked] = useState(!!data.isLocked)
  const [isMinimized, setIsMinimized] = useState(!!data.isMinimized)
  const [minimizeLevel, setMinimizeLevel] = useState<'normal' | 'medium' | 'minimum'>(data.minimizeLevel || 'normal')
  const [selectedTemplate, setSelectedTemplate] = useState(data.selectedTemplate || '')
  const [customOperation, setCustomOperation] = useState(data.customOperation || '')
  const [llmPrompt, setLlmPrompt] = useState(data.llmPrompt || '')
  const [templateConfig, setTemplateConfig] = useState(data.templateConfig || null)
  const [inputMode, setInputMode] = useState<'strict' | 'loose'>(data.inputMode || 'loose')
  const [outputMode, setOutputMode] = useState<'strict' | 'loose'>(data.outputMode || 'loose')
  const [inputJsonStructure, setInputJsonStructure] = useState(data.inputJsonStructure || '')
  const [outputJsonStructure, setOutputJsonStructure] = useState(data.outputJsonStructure || '')
  const [inputFormatPrompt, setInputFormatPrompt] = useState(data.inputFormatPrompt || '')
  const [outputFormatPrompt, setOutputFormatPrompt] = useState(data.outputFormatPrompt || '')
  const [outputText, setOutputText] = useState(data.outputText || '')
  const [automationEnabled, setAutomationEnabled] = useState(!!data.automationEnabled)
  const [schedule, setSchedule] = useState(data.schedule || null)
  const [assignedEmails, setAssignedEmails] = useState<string[]>(
    Array.isArray(data.assignedEmails) ? data.assignedEmails : 
    (data.assignedEmails ? String(data.assignedEmails).trim().split(/\s+/).filter(Boolean) : [])
  )

  // Dialog states
  const [isDataSettingsDialogVisible, setIsDataSettingsDialogVisible] = useState(false)
  const [isSchedulingDialogVisible, setIsSchedulingDialogVisible] = useState(false)
  const [showUsageStats, setShowUsageStats] = useState(false)

  // Initialize usage counters
  const [usageCounters, setUsageCounters] = useState<UsageCounters>(
    data.usageCounters || {
      totalRuns: 0,
      successfulRuns: 0,
      failedRuns: 0,
      avgProcessingTimeMs: 0,
      avgInputSizeBytes: 0,
      avgOutputSizeBytes: 0,
      totalDataProcessedBytes: 0
    }
  )

  // --- Data Settings logic ---
  const outgoingFields = {
    outputText: {
      value: outputText,
      enabled: true,
      label: 'Output Text',
      description: 'Processed output from AI operation',
      permissions: { readable: true, writable: false },
    },
    usageStats: {
      value: JSON.stringify(usageCounters),
      enabled: true,
      label: 'Usage Statistics',
      description: 'Node usage and performance statistics',
      permissions: { readable: true, writable: false },
    },
  }

  const {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleOpenDataSettingsDialog,
  } = useDataSettings(
    id,
    data,
    (newData) => updateNodeData(id, { ...data, ...newData }),
    false,
    '',
    new Map(),
    '',
    setIsDataSettingsDialogVisible
  )

  // Keep outgoing/exposedData up to date
  useEffect(() => {
    const newDataSettings = {
      ...dataSettings,
      inputLinks: dataSettings?.inputLinks || [],
      exposedData: outgoingFields,
      availableNodes: [],
      outputLinks: dataSettings?.outputLinks || [],
      autoSync: false,
    }
    if (JSON.stringify(dataSettings?.exposedData) !== JSON.stringify(outgoingFields)) {
      setDataSettings(newDataSettings)
      updateNodeData(id, { ...data, dataSettings: newDataSettings })
    }
  }, [outputText, usageCounters])

  // Initialize minimize level from data
  useEffect(() => {
    const level = (data.minimizeLevel!) || 'normal'
    setMinimizeLevel(level)
    setIsMinimized(level !== 'normal')
  }, [data.minimizeLevel])

  // Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {}
    
    if (selectedTemplate) {
      minifiedInfo.template = selectedTemplate
    }
    
    if (usageCounters.totalRuns > 0) {
      minifiedInfo.runs = usageCounters.totalRuns
    }
    
    if (outputText && outputText.trim()) {
      minifiedInfo.output = outputText.substring(0, 50) + (outputText.length > 50 ? '...' : '')
    }
    
    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {}
    const hasChanges = JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo)
    
    if (hasChanges) {
      updateNodeData(id, { ...data, minifiedInfo })
    }
  }, [selectedTemplate, usageCounters.totalRuns, outputText, id])

  // Node action handlers
  const handleLock = () => {
    const newLockState = !isLocked
    setIsLocked(newLockState)
    updateNodeData(id, { ...data, isLocked: newLockState })
  }

  const handleMinimize = () => {
    const newMinState = !isMinimized
    setIsMinimized(newMinState)
    setMinimizeLevel(newMinState ? 'medium' : 'normal')
    updateNodeData(id, { 
      ...data, 
      isMinimized: newMinState,
      minimizeLevel: newMinState ? 'medium' : 'normal'
    })
  }

  const handleMinimizeLevelChange = (level: 'normal' | 'medium' | 'minimum') => {
    setMinimizeLevel(level)
    setIsMinimized(level !== 'normal')
    updateNodeData(id, { 
      ...data, 
      minimizeLevel: level,
      isMinimized: level !== 'normal'
    })
  }

  const handleTitleChange = (newTitle: string) => {
    updateNodeData(id, { 
      ...data, 
      label: newTitle
    })
  }

  // --- AI Command State ---
  const command = data.documentCommand
  const { isProcessing, statusMessage } = useAICommandState(command as any, AICommandType.NODE_RESOURCE)

  // --- Manual trigger for AI processing ---
  const handleRunOperation = () => {
    const operationText = selectedTemplate || customOperation
    if (!operationText.trim()) {
      toast.error('Please select a template or enter a custom operation')
      return
    }

    const startTime = Date.now()
    
    updateNodeData(id, {
      ...data,
      selectedTemplate,
      customOperation,
      llmPrompt,
      inputMode,
      outputMode,
      inputJsonStructure,
      outputJsonStructure,
      inputFormatPrompt,
      outputFormatPrompt,
      automationEnabled,
      schedule,
      documentCommand: createAICommand(AICommandType.NODE_RESOURCE, { 
        operationType: selectedTemplate || 'custom',
        operationText: operationText,
        llmPrompt,
        inputMode,
        outputMode,
        inputJsonStructure: inputMode === 'strict' ? inputJsonStructure : undefined,
        outputJsonStructure: outputMode === 'strict' ? outputJsonStructure : undefined,
        inputFormatPrompt: inputMode === 'loose' ? inputFormatPrompt : undefined,
        outputFormatPrompt: outputMode === 'loose' ? outputFormatPrompt : undefined,
        startTime
      })
    })
  }

  // Get selected template details
  const getSelectedTemplateDetails = () => {
    if (!selectedTemplate) return null
    
    for (const [category, templates] of Object.entries(AI_TEMPLATES)) {
      const template = templates.find(t => t.value === selectedTemplate)
      if (template) {
        return { ...template, category }
      }
    }
    return null
  }

  const templateDetails = getSelectedTemplateDetails()

  // Handle template selection and auto-populate fields
  const handleTemplateSelection = (templateValue: string) => {
    setSelectedTemplate(templateValue)
    
    if (templateValue) {
      const template = getSelectedTemplateDetails() || (() => {
        for (const [category, templates] of Object.entries(AI_TEMPLATES)) {
          const found = templates.find(t => t.value === templateValue)
          if (found) return found
        }
        return null
      })()
      
      if (template) {
        setLlmPrompt(template.llm_prompt || '')
        setTemplateConfig(template)
        
        // Auto-populate JSON structures for strict mode
        if (template.inputs) {
          const inputStructure = JSON.stringify(template.inputs, null, 2)
          setInputJsonStructure(inputStructure)
        }
        
        if (template.output) {
          const outputStructure = JSON.stringify(template.output, null, 2)
          setOutputJsonStructure(outputStructure)
        }
        
        updateNodeData(id, { 
          ...data, 
          selectedTemplate: templateValue,
          llmPrompt: template.llm_prompt || '',
          templateConfig: template,
          inputJsonStructure: template.inputs ? JSON.stringify(template.inputs, null, 2) : '',
          outputJsonStructure: template.output ? JSON.stringify(template.output, null, 2) : ''
        })
      }
    } else {
      setLlmPrompt('')
      setTemplateConfig(null)
      setInputJsonStructure('')
      setOutputJsonStructure('')
      updateNodeData(id, { 
        ...data, 
        selectedTemplate: templateValue,
        llmPrompt: '',
        templateConfig: null,
        inputJsonStructure: '',
        outputJsonStructure: ''
      })
    }
  }

  // Copy output to clipboard
  const handleCopyOutput = () => {
    if (outputText) {
      navigator.clipboard.writeText(outputText).then(
        () => toast.success('Output copied to clipboard'),
        () => toast.error('Failed to copy to clipboard')
      )
    }
  }

  // Update usage statistics when processing completes
  useEffect(() => {
    if (data.documentCommand?.['sysagent-response']?.includes('Complete') && data.outputText !== outputText) {
      setOutputText(data.outputText || '')
      
      // Update usage stats
      const newCounters = {
        ...usageCounters,
        totalRuns: usageCounters.totalRuns + 1,
        successfulRuns: usageCounters.successfulRuns + 1,
        lastRunTimestamp: Date.now()
      }
      setUsageCounters(newCounters)
      updateNodeData(id, { ...data, usageCounters: newCounters })
    }
  }, [data.documentCommand, data.outputText])

  // Compute status for NodeBaseContainer
  const nodeStatus = useMemo(() => {
    if (isProcessing) return "loading"
    if (statusMessage && statusMessage.toLowerCase().includes("error")) return "error"
    if (outputText) return "success"
    return "initial"
  }, [isProcessing, statusMessage, outputText])

  return (
    <>
      <NodeBaseContainer
        id={id}
        data={data}
        resizable={!isLocked}
        minWidth={400}
        minHeight={300}
        isLocked={isLocked}
        isMinimized={isMinimized}
        minimizeLevel={minimizeLevel}
        nodeTitle={String(data.label || 'AI Knot')}
        onLockChange={handleLock}
        onMinimizeChange={handleMinimize}
        onMinimizeLevelChange={handleMinimizeLevelChange}
        onTitleChange={handleTitleChange}
        assignedEmails={assignedEmails}
        onAssignUsersClick={() => {
          const emailsStr = window.prompt('Enter emails separated by space', assignedEmails.join(' '))
          if (emailsStr !== null) {
            const emailsArr = emailsStr.trim().split(/\s+/).filter(Boolean)
            setAssignedEmails(emailsArr)
            updateNodeData(id, { ...data, assignedEmails: emailsArr })
          }
        }}
        onDataSettingsClick={() => setIsDataSettingsDialogVisible(true)}
        status={nodeStatus}
        selected={selected}
        customButtons={[
          {
            id: 'schedule',
            onClick: () => setIsSchedulingDialogVisible(true),
            icon: <Calendar size={16} />,
            tooltip: 'Scheduling',
          },
          {
            id: 'stats',
            onClick: () => setShowUsageStats(!showUsageStats),
            icon: <BarChart3 size={16} />,
            tooltip: 'Usage Statistics',
          },
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {minimizeLevel === 'normal' && (
            <>
              {/* Template Selection */}
              <div style={{ marginBottom: 16 }}>
                <label className="block text-xs font-semibold mb-2">
                  AI Operation Template
                </label>
                <select
                  className="w-full border rounded p-2 nodrag nowheel"
                  value={selectedTemplate}
                  onChange={(e) => {
                    handleTemplateSelection(e.target.value)
                  }}
                  disabled={isLocked}
                >
                  <option value="">Select a template...</option>
                  {Object.entries(AI_TEMPLATES).map(([category, templates]) => (
                    <optgroup key={category} label={category}>
                      {templates.map((template) => (
                        <option key={template.value} value={template.value}>
                          {template.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {templateDetails && (
                  <div className="text-xs text-gray-600 mt-1">
                    {templateDetails.description}
                  </div>
                )}
              </div>

              {/* LLM Prompt Field */}
              <div style={{ marginBottom: 16 }}>
                <label className="block text-xs font-semibold mb-2">
                  LLM Prompt Instructions
                </label>
                <textarea
                  className="w-full border rounded p-2 nodrag nowheel"
                  placeholder="Detailed instructions for the AI to follow when processing the input data..."
                  value={llmPrompt}
                  onChange={(e) => setLlmPrompt(e.target.value)}
                  onBlur={(e) => updateNodeData(id, { ...data, llmPrompt: e.target.value })}
                  disabled={isLocked}
                  style={{ minHeight: '60px', resize: 'vertical' }}
                />
                <div className="text-xs text-gray-500 mt-1">
                  This prompt will be sent to the AI along with the input data
                </div>
              </div>

              {/* Input/Output Configuration */}
              {templateConfig && (
                <div style={{ marginBottom: 16 }}>
                  <label className="block text-xs font-semibold mb-2">
                    Template Configuration
                  </label>
                  <div className="border rounded p-3 bg-gray-50">
                    {/* Input Configuration */}
                    <div className="mb-3">
                      <div className="text-xs font-semibold text-gray-700 mb-1">Expected Inputs:</div>
                      {Array.isArray(templateConfig.inputs?.data) ? (
                        templateConfig.inputs.data.map((input: any, idx: number) => (
                          <div key={idx} className="text-xs text-gray-600 mb-1">
                            • <strong>{input.name}</strong> ({input.type}): {input.prompt}
                          </div>
                        ))
                      ) : templateConfig.inputs?.data ? (
                        <div className="text-xs text-gray-600 mb-1">
                          • <strong>{templateConfig.inputs.data.name}</strong> ({templateConfig.inputs.data.type}): {templateConfig.inputs.data.prompt}
                        </div>
                      ) : null}
                    </div>
                    
                    {/* Output Configuration */}
                    <div>
                      <div className="text-xs font-semibold text-gray-700 mb-1">Expected Output:</div>
                      {templateConfig.output?.data && (
                        <div className="text-xs text-gray-600">
                          • <strong>{templateConfig.output.data.name}</strong> ({templateConfig.output.data.type}): {templateConfig.output.data.prompt}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Custom Operation */}
              <div style={{ marginBottom: 16, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <label className="block text-xs font-semibold mb-2">
                  Custom Operation
                </label>
                <textarea
                  className="w-full border rounded p-2 flex-1 nodrag nowheel"
                  placeholder="Write your custom operation here, or select a template above. This should describe what you want to do with the input data to produce structured output."
                  value={customOperation}
                  onChange={(e) => setCustomOperation(e.target.value)}
                  onBlur={(e) => updateNodeData(id, { ...data, customOperation: e.target.value })}
                  disabled={isLocked}
                  style={{ minHeight: '80px', resize: 'vertical' }}
                />
              </div>

              {/* Input/Output Mode Settings */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label className="text-xs font-semibold mb-2 block">Input Processing</label>
                    <select
                      className="w-full border rounded p-2 mb-3"
                      value={inputMode}
                      onChange={(e) => {
                        const mode = e.target.value as 'strict' | 'loose'
                        setInputMode(mode)
                        updateNodeData(id, { ...data, inputMode: mode })
                      }}
                      disabled={isLocked}
                    >
                      <option value="loose">Loose (AI decides format)</option>
                      <option value="strict">Strict (JSON validation)</option>
                    </select>
                    
                    {inputMode === 'strict' ? (
                      <div>
                        <label className="text-xs font-semibold mb-1 block">Expected Input JSON Structure</label>
                        <textarea
                          className="w-full border rounded p-2 text-xs font-mono"
                          placeholder='{\n  "type": "object",\n  "properties": {\n    "field1": "value1"\n  }\n}'
                          value={inputJsonStructure}
                          onChange={(e) => setInputJsonStructure(e.target.value)}
                          onBlur={(e) => updateNodeData(id, { ...data, inputJsonStructure: e.target.value })}
                          disabled={isLocked}
                          style={{ minHeight: '100px', resize: 'vertical' }}
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          Define the exact JSON structure expected for input validation
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="text-xs font-semibold mb-1 block">Input Format Guidance</label>
                        <textarea
                          className="w-full border rounded p-2 text-xs"
                          placeholder="Additional instructions for AI on how to process and format input data..."
                          value={inputFormatPrompt}
                          onChange={(e) => setInputFormatPrompt(e.target.value)}
                          onBlur={(e) => updateNodeData(id, { ...data, inputFormatPrompt: e.target.value })}
                          disabled={isLocked}
                          style={{ minHeight: '80px', resize: 'vertical' }}
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          Give AI hints on how to handle input data formatting
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="text-xs font-semibold mb-2 block">Output Processing</label>
                    <select
                      className="w-full border rounded p-2 mb-3"
                      value={outputMode}
                      onChange={(e) => {
                        const mode = e.target.value as 'strict' | 'loose'
                        setOutputMode(mode)
                        updateNodeData(id, { ...data, outputMode: mode })
                      }}
                      disabled={isLocked}
                    >
                      <option value="loose">Loose (flexible output)</option>
                      <option value="strict">Strict (validated structure)</option>
                    </select>
                    
                    {outputMode === 'strict' ? (
                      <div>
                        <label className="text-xs font-semibold mb-1 block">Expected Output JSON Structure</label>
                        <textarea
                          className="w-full border rounded p-2 text-xs font-mono"
                          placeholder='{\n  "type": "object",\n  "properties": {\n    "result": "processed_data"\n  }\n}'
                          value={outputJsonStructure}
                          onChange={(e) => setOutputJsonStructure(e.target.value)}
                          onBlur={(e) => updateNodeData(id, { ...data, outputJsonStructure: e.target.value })}
                          disabled={isLocked}
                          style={{ minHeight: '100px', resize: 'vertical' }}
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          Define the exact JSON structure for output validation
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="text-xs font-semibold mb-1 block">Output Format Guidance</label>
                        <textarea
                          className="w-full border rounded p-2 text-xs"
                          placeholder="Additional instructions for AI on how to structure and format output..."
                          value={outputFormatPrompt}
                          onChange={(e) => setOutputFormatPrompt(e.target.value)}
                          onBlur={(e) => updateNodeData(id, { ...data, outputFormatPrompt: e.target.value })}
                          disabled={isLocked}
                          style={{ minHeight: '80px', resize: 'vertical' }}
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          Guide AI on how to format and structure the output
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Automation Settings */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 8 }}>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={automationEnabled}
                      onChange={(e) => {
                        setAutomationEnabled(e.target.checked)
                        updateNodeData(id, { ...data, automationEnabled: e.target.checked })
                      }}
                      disabled={isLocked}
                      className="nodrag nowheel"
                    />
                    <span className="text-xs">Enable automation</span>
                  </label>
                  {automationEnabled && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIsSchedulingDialogVisible(true)}
                      disabled={isLocked}
                    >
                      <Calendar className="mr-1 h-3 w-3" />
                      Schedule...
                    </Button>
                  )}
                </div>
                {schedule && (
                  <div className="text-xs text-gray-500">
                    <strong>Schedule:</strong> {schedule.summary || 'Custom schedule set'}
                  </div>
                )}
              </div>

              {/* Output Display */}
              <div style={{ marginBottom: 16, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label className="text-xs font-semibold">Output</label>
                  {outputText && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyOutput}
                      disabled={!outputText}
                    >
                      <Copy className="mr-1 h-3 w-3" />
                      Copy
                    </Button>
                  )}
                </div>
                <textarea
                  className="w-full border rounded p-2 flex-1 nodrag nowheel"
                  placeholder="Processed output will appear here..."
                  value={outputText}
                  readOnly
                  style={{ minHeight: '80px', backgroundColor: '#f8f9fa' }}
                />
              </div>

              {/* Usage Statistics */}
              {showUsageStats && (
                <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f8f9fa', borderRadius: 4 }}>
                  <div className="text-xs font-semibold mb-2">Usage Statistics</div>
                  <div className="text-xs space-y-1">
                    <div>Total runs: {usageCounters.totalRuns}</div>
                    <div>Successful: {usageCounters.successfulRuns}</div>
                    <div>Failed: {usageCounters.failedRuns}</div>
                    <div>Avg processing: {usageCounters.avgProcessingTimeMs}ms</div>
                    <div>Total data processed: {Math.round(usageCounters.totalDataProcessedBytes / 1024)}KB</div>
                  </div>
                </div>
              )}

              {/* Status Message */}
              {statusMessage && (
                <div style={{ marginBottom: 16 }}>
                  <div className="text-sm text-gray-600">
                    {statusMessage}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Action Button - always at bottom */}
          {minimizeLevel === 'normal' && (
            <div style={{ marginTop: 'auto' }}>
              <Button
                className="w-full"
                onClick={handleRunOperation}
                disabled={isLocked || isProcessing || (!selectedTemplate && !customOperation.trim())}
              >
                <Play className="mr-2 h-4 w-4" />
                {isProcessing ? 'Processing...' : 'Run Operation'}
              </Button>
            </div>
          )}

        </div>
      </NodeBaseContainer>

      {/* Scheduling Dialog */}
      {isSchedulingDialogVisible && (
        <SchedulingDialog
          visible={isSchedulingDialogVisible}
          schedule={schedule}
          onSave={(sched) => {
            setSchedule(sched)
            updateNodeData(id, { ...data, schedule: sched })
            setIsSchedulingDialogVisible(false)
          }}
          onHide={() => setIsSchedulingDialogVisible(false)}
        />
      )}

      {/* Data Settings Dialog */}
      <NodeDataSettingsDialog
        visible={isDataSettingsDialogVisible}
        onHide={() => setIsDataSettingsDialogVisible(false)}
        settings={dataSettings}
        nodeId={id}
        onSettingsChange={handleDataSettingsChange}
        onRemoveInputLink={async () => ({ success: true })}
        onSyncInputLink={async () => ({ success: true })}
        forceShowAvailableNodes={false}
        debug={false}
      />
    </>
  )
}

export default memo(NodeAIKnot)